import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { HelpCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultContent = {
  title: "Frequently Asked Questions",
  subtitle: "Find answers to common questions about ordering, shipping, and more",
  categories: [
    {
      category: 'Orders & Shipping',
      questions: [
        { q: 'How long does shipping take?', a: 'We deliver within 5-7 business days across India. Express delivery is available in metro cities (2-3 days).' },
        { q: 'Do you offer free shipping?', a: 'Yes! We offer free shipping on all orders across India.' },
        { q: 'Can I track my order?', a: 'Absolutely! Once your order ships, you will receive a tracking number via email and SMS. You can also track your order using our Track Order page.' },
        { q: 'What if I\'m not home during delivery?', a: 'Our delivery partners will attempt delivery 2-3 times. If unsuccessful, you can arrange a convenient delivery time or pick up from the nearest delivery center.' }
      ]
    },
    {
      category: 'Payment',
      questions: [
        { q: 'Is Cash on Delivery (COD) available?', a: 'Yes! COD is available for all orders. You can pay in cash when you receive the product.' },
        { q: 'What payment methods do you accept?', a: 'We accept all major payment methods including UPI, Credit/Debit Cards, Net Banking, Wallets, and Cash on Delivery.' },
        { q: 'Is my payment information secure?', a: 'Yes, all transactions are processed through secure payment gateways with 256-bit encryption. We never store your card details.' },
        { q: 'Can I pay using EMI?', a: 'EMI options are available for orders above ₹3,000 through select credit cards and payment partners.' }
      ]
    },
    {
      category: 'Returns & Refunds',
      questions: [
        { q: 'What is your return policy?', a: 'We offer a 7-day return policy. If you\'re not satisfied with your purchase, you can return it within 7 days for a full refund.' },
        { q: 'How do I return a product?', a: 'Contact our support team with your order ID. We will arrange a free pickup and process your refund once we receive the product.' },
        { q: 'When will I receive my refund?', a: 'Refunds are processed within 5-7 business days after we receive the returned product. The amount will be credited to your original payment method.' },
        { q: 'Are there any items that cannot be returned?', a: 'Products must be unused, in original packaging with all tags attached. Certain items like personal care products may not be eligible for return for hygiene reasons.' }
      ]
    },
    {
      category: 'Products',
      questions: [
        { q: 'Are your products genuine?', a: 'Yes, 100% genuine products with manufacturer warranty. We source directly from authorized distributors.' },
        { q: 'Do products come with warranty?', a: 'Yes, all products come with manufacturer warranty. Warranty duration varies by product and is mentioned on the product page.' },
        { q: 'What if I receive a damaged product?', a: 'We carefully pack all products, but if you receive a damaged item, contact us immediately with photos. We will arrange a replacement or full refund.' },
        { q: 'Can I see product reviews?', a: 'Yes, each product page displays verified customer reviews and ratings to help you make informed decisions.' }
      ]
    },
    {
      category: 'Account & Support',
      questions: [
        { q: 'Do I need to create an account to place an order?', a: 'No, we offer guest checkout. However, creating an account helps you track orders easily and get exclusive offers.' },
        { q: 'How can I contact customer support?', a: 'You can reach us via email at support@indicraft.in, call us at +91 123-456-7890, or chat with us on WhatsApp.' },
        { q: 'What are your customer support hours?', a: 'Monday-Saturday: 9 AM - 7 PM, Sunday: 10 AM - 5 PM (IST). We respond to emails within 24 hours.' },
        { q: 'Can I modify or cancel my order?', a: 'Orders can be modified or cancelled within 2 hours of placement. After that, the order enters processing and cannot be changed.' }
      ]
    }
  ],
  cta_title: "Still Have Questions?",
  cta_subtitle: "Can't find what you're looking for? Our customer support team is here to help!",
  cta_button_text: "Contact Support",
  meta_title: "FAQ | Indicraft",
  meta_description: "Find answers to frequently asked questions about Indicraft orders, shipping, returns, and more."
};

const FAQPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    document.title = content.meta_title || "FAQ | Indicraft";
  }, [content]);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cms/faq`);
      if (response.data && response.data.categories && response.data.categories.length > 0) {
        setContent({ ...defaultContent, ...response.data });
      }
    } catch (error) {
      console.log('Using default content for FAQ page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-white to-[#F8FAFC] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-[#F0FDF4] flex items-center justify-center">
                  <HelpCircle className="w-8 h-8 text-[#064E3B]" />
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" data-testid="faq-title">
                {content.title}
              </h1>
              <p className="text-lg text-zinc-600">
                {content.subtitle}
              </p>
            </div>

            <div className="space-y-8">
              {content.categories.map((category, catIndex) => (
                <div key={catIndex} className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-8" data-testid={`faq-category-${catIndex}`}>
                  <h2 className="text-2xl font-semibold text-[#0f172a] mb-6">{category.category}</h2>
                  
                  <Accordion type="single" collapsible className="w-full">
                    {category.questions.map((faq, qIndex) => (
                      <AccordionItem key={qIndex} value={`${catIndex}-${qIndex}`}>
                        <AccordionTrigger className="text-left font-medium text-[#0f172a] hover:text-[#064E3B]">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-zinc-600 leading-relaxed">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-[#F0FDF4] border border-[#86EFAC] rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-semibold text-[#0f172a] mb-3">{content.cta_title}</h3>
              <p className="text-zinc-700 mb-6">
                {content.cta_subtitle}
              </p>
              <Button
                onClick={() => navigate('/contact')}
                className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl"
                data-testid="faq-cta-button"
              >
                {content.cta_button_text}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
